import React, { useEffect } from 'react';
import { useParams, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isValidLanguage, DEFAULT_LANGUAGE } from '../config/languages';

/**
 * LanguageLayout Component
 * 
 * This component:
 * 1. Validates the language code from the URL
 * 2. Syncs the language with i18n
 * 3. Updates document attributes (lang, dir)
 * 4. Redirects invalid language codes to default language
 */
const LanguageLayout = () => {
    const { lang } = useParams();
    const { i18n } = useTranslation();
    const location = useLocation();

    // Sync language with i18n and update document attributes
    // This must be called before any conditional returns (Rules of Hooks)
    useEffect(() => {
        // Only execute if language is valid
        if (isValidLanguage(lang)) {
            if (i18n.language !== lang) {
                i18n.changeLanguage(lang);
            }

            // Update document language and direction
            document.documentElement.lang = lang;

            // Set direction based on language (for future RTL support like Arabic)
            const direction = lang === 'ar' ? 'rtl' : 'ltr';
            document.documentElement.dir = direction;
        }
    }, [lang, i18n]);

    // Validate language code
    if (!isValidLanguage(lang)) {
        // Redirect to default language with the same path
        const pathWithoutLang = location.pathname.split('/').slice(2).join('/');
        const newPath = `/${DEFAULT_LANGUAGE}${pathWithoutLang ? '/' + pathWithoutLang : ''}${location.search}${location.hash}`;
        return <Navigate to={newPath} replace />;
    }

    return <Outlet />;
};

export default LanguageLayout;
